import React, { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { SALON } from "../data/salonData";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/75 backdrop-blur-xl border-b border-softPink/60 shadow-soft"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        {/* Logo */}
        <a href="#home" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <img
            src="/brand/logo.png"
            alt="Kinjal's Beauty & Hair logo"
            className="h-14 sm:h-16 w-auto"
            style={{ filter: "contrast(1.15) saturate(1.5) drop-shadow(0 2px 6px #ffd9db) drop-shadow(0 0 10px #ffd9db)" }}
            loading="eager"
          />
        </a>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium tracking-wide text-luxuryBlack/80 hover:text-roseGold transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-roseGold after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${SALON.phoneTel}`}
            data-testid="navbar-call-btn"
            className="btn-primary text-sm"
          >
            <Phone className="w-4 h-4" /> Call {SALON.phoneDisplay}
          </a>
        </div>

        {/* Mobile burger */}
        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-luxuryBlack/15 bg-white/70 backdrop-blur"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-softPink/60 px-6 pb-8 pt-4 animate-fade-in"
        >
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={close}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="py-3 px-2 text-base font-medium border-b border-beigeNude text-luxuryBlack/85 hover:text-roseGold"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="mt-5">
            <a
              href={`tel:${SALON.phoneTel}`}
              onClick={close}
              data-testid="mobile-call-btn"
              className="btn-primary w-full justify-center text-sm"
            >
              <Phone className="w-4 h-4" /> Call {SALON.phoneDisplay}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
